import React, { useState, useEffect, useCallback } from 'react';
import Icon from 'components/AppIcon';
import { getPendingCount, processPendingQueue, onQueueStatusChange } from '../lib/indexedDBQueue';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';

const QueueStatusIndicator = ({ className = '' }) => {
  const { user } = useAuth();
  const [pendingCount, setPendingCount] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const [showFailedModal, setShowFailedModal] = useState(false);
  const [failedOps, setFailedOps] = useState([]);
  const [lastSyncResult, setLastSyncResult] = useState(null);

  useEffect(() => {
    getPendingCount()?.then(setPendingCount);
    const unsubscribe = onQueueStatusChange(setPendingCount);
    return unsubscribe;
  }, []);

  const handleSyncNow = useCallback(async () => {
    if (!user?.id || syncing || !navigator.onLine) return;
    setSyncing(true);
    try {
      const result = await processPendingQueue({
        supabase,
        userId: user?.id,
        onProgress: () => {},
      });
      setLastSyncResult(result);
      if (result?.failedOps?.length > 0) {
        setFailedOps(result?.failedOps);
      }
    } catch (err) {
      console.error('[QueueStatus] Sync error:', err);
    } finally {
      setSyncing(false);
    }
  }, [user?.id, syncing]);

  if (pendingCount === 0 && !lastSyncResult) return null;

  return (
    <>
      <div className={`flex items-center gap-2 ${className}`}>
        {pendingCount > 0 && (
          <button
            onClick={handleSyncNow}
            disabled={syncing || !navigator.onLine}
            className="flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium transition-all disabled:opacity-50"
            style={{
              background: 'rgba(251,191,36,0.1)',
              border: '1px solid rgba(251,191,36,0.25)',
              color: '#FBBF24',
            }}
            title={`${pendingCount} pending operation${pendingCount !== 1 ? 's' : ''} — click to sync`}
          >
            <Icon
              name={syncing ? 'Loader2' : 'CloudUpload'}
              size={12}
              color="#FBBF24"
            />
            <span>{pendingCount} pending</span>
          </button>
        )}
        {lastSyncResult?.processed > 0 && pendingCount === 0 && (
          <span className="flex items-center gap-1 text-xs" style={{ color: 'var(--color-success, #10B981)' }}>
            <Icon name="CheckCircle" size={12} color="var(--color-success, #10B981)" />
            {lastSyncResult?.processed} synced
          </span>
        )}
        {lastSyncResult?.failedOps?.length > 0 && (
          <button
            onClick={() => setShowFailedModal(true)}
            className="flex items-center gap-1 text-xs"
            style={{ color: '#EF4444' }}
          >
            <Icon name="AlertCircle" size={12} color="#EF4444" />
            {lastSyncResult?.failedOps?.length} failed
          </button>
        )}
      </div>
      {showFailedModal && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.7)' }}>
          <div className="w-full max-w-md rounded-xl border border-border bg-card p-5 shadow-2xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-foreground">Failed Operations</h3>
              <button
                onClick={() => setShowFailedModal(false)}
                className="w-7 h-7 rounded flex items-center justify-center hover:bg-white/5 transition-colors"
              >
                <Icon name="X" size={14} color="var(--color-muted-foreground)" />
              </button>
            </div>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {failedOps?.map((op, i) => (
                <div key={i} className="p-3 rounded-lg border border-border/50" style={{ background: 'rgba(239,68,68,0.05)' }}>
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-foreground capitalize">{op?.type}</span>
                    <span className="text-xs" style={{ color: 'rgba(239,68,68,0.8)' }}>{op?.reason}</span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1 truncate">
                    {op?.type === 'trade' && `${op?.payload?.order_type} ${op?.payload?.quantity}x @ $${op?.payload?.price}`}
                    {op?.type === 'message' && op?.payload?.message_text}
                    {op?.type === 'crime' && op?.payload?.crime_key}
                  </p>
                </div>
              ))}
            </div>
            <button
              onClick={() => { setShowFailedModal(false); setLastSyncResult(null); setFailedOps([]); }}
              className="mt-4 w-full py-2 rounded-lg border border-border text-sm text-muted-foreground hover:text-foreground transition-colors"
              style={{ background: 'var(--color-card)' }}
            >
              Dismiss
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default QueueStatusIndicator;
